import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ShoppingCart, LogOut, User, Shield } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";

const links = [
  { to: "/about", label: "About" },
  { to: "/shop", label: "Shop" },
  { to: "/wholesale", label: "Wholesale" },
  { to: "/research-library", label: "Research" },
  { to: "/faqs", label: "FAQs" },
  { to: "/refund-policy", label: "Refund Policy" },
  { to: "/contact", label: "Contact" },
];

export default function Nav() {
  const { user, logout } = useAuth();
  const { count, setOpen } = useCart();
  const { pathname } = useLocation();
  const renderLink = (l) => (
    <NavLink
      key={l.to}
      to={l.to}
      data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}-link`}
      className={({ isActive }) =>
        `font-mono-tech uppercase transition-colors whitespace-nowrap text-[9px] tracking-[0.1em] sm:text-[11px] sm:tracking-[0.18em] ${
          isActive ? "text-white border-b border-[#00C3FF] pb-0.5" : "text-[#00C3FF] hover:text-white"
        }`
      }
    >
      {l.label}
    </NavLink>
  );

  return (
    <header data-testid="site-nav" className="sticky top-0 z-40 glass">
      <div className="max-w-7xl mx-auto px-6 py-4">
        {/* Row 1 — Logo on top, action icons on the right */}
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 sm:gap-3 group min-w-0 flex-1 sm:flex-none" data-testid="nav-home-link" aria-label="ProSource Labs home">
            <img src="/brand/psl-monogram-header.png" alt="PSL monogram" className="h-[52px] sm:h-20 w-auto shrink-0" />
            <img src="/brand/psl-wordmark-header.png" alt="Pro Source Labs Peptides" className="min-w-0 w-full max-w-[170px] h-auto sm:w-auto sm:max-w-none sm:h-12 object-contain object-left transition-opacity group-hover:opacity-85" />
          </Link>

          <div className="flex items-center gap-0 sm:gap-4 shrink-0 ml-2">
            <button
              data-testid="nav-cart-button"
              onClick={() => setOpen(true)}
              className="relative p-1.5 sm:p-2 text-white hover:text-[#00C3FF] transition-colors"
              aria-label="Cart"
            >
              <ShoppingCart size={20} />
              {count > 0 && (
                <span
                  data-testid="nav-cart-count"
                  className="absolute -top-1 -right-1 bg-[#00C3FF] text-[#05050A] text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center font-mono-tech"
                >
                  {count}
                </span>
              )}
            </button>

            {user && (
              <div className="flex items-center gap-2">
                {user.role === "admin" && (
                  <Link
                    to="/admin"
                    data-testid="nav-admin-link"
                    className="hidden sm:inline-flex items-center gap-1 px-3 py-1.5 border border-[#00C3FF]/40 text-[#00C3FF] text-[10px] font-mono-tech uppercase tracking-[0.18em] hover:bg-[#00C3FF]/10 transition-colors"
                    title="Admin Console"
                  >
                    <Shield size={11} /> Admin
                  </Link>
                )}
                <Link
                  to="/account/orders"
                  data-testid="nav-account-link"
                  className="p-1.5 sm:p-2 text-white/60 hover:text-[#00C3FF] transition-colors"
                  aria-label="Account"
                  title={user.email}
                >
                  <User size={18} />
                </Link>
                <button
                  data-testid="nav-logout-button"
                  onClick={logout}
                  className="p-1.5 sm:p-2 text-white/60 hover:text-[#00C3FF] transition-colors"
                  aria-label="Logout"
                  title="Logout"
                >
                  <LogOut size={18} />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Row 2 — Nav menu under the logo, in brand blue */}
        <nav data-testid="nav-links" className="mt-3 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-8 sm:gap-y-2">
          <div className="flex flex-wrap justify-between gap-x-3 gap-y-2 sm:contents">
            {links.slice(0, 4).map(renderLink)}
          </div>
          <div className="flex flex-wrap justify-between gap-x-3 gap-y-2 sm:contents">
            {links.slice(4).map(renderLink)}
          {user && (
            <NavLink
              to="/wholesale-shop"
              data-testid="nav-wholesale-shop-link"
              className={({ isActive }) =>
                `font-mono-tech uppercase transition-colors whitespace-nowrap text-[9px] tracking-[0.1em] sm:text-[11px] sm:tracking-[0.18em] flex items-center gap-1.5 ${
                  isActive
                    ? "text-white border-b border-[#00C3FF] pb-0.5"
                    : "text-[#00C3FF] hover:text-white"
                }`
              }
              
            >
              <span className="inline-block w-1.5 h-1.5 bg-[#00C3FF]" />
              Wholesale Catalog
            </NavLink>
          )}
          </div>
        </nav>
        {pathname !== "/" && (
          <div data-testid="nav-motto" className="mt-3 pt-2.5 border-t border-white/5">
            <span className="whitespace-nowrap text-[9px] sm:text-[10px] uppercase tracking-[0.16em] sm:tracking-[0.3em] bg-clip-text text-transparent"
              style={{ fontFamily: "Michroma, sans-serif", backgroundImage: "linear-gradient(180deg,#FFFFFF 0%,#BFE9FF 45%,#00C3FF 100%)" }}>
              Precision. Purity. Performance.
            </span>
          </div>
        )}
      </div>
    </header>
  );
}
